import { FC, useCallback, useState } from 'react';
import html2canvas from 'html2canvas';
import {
	Box,
	Card,
	CardContent,
	CardHeader,
	CircularProgress,
	Divider,
	IconButton,
	Skeleton,
	Tooltip,
	Typography,
} from '@mui/material';
import AddBoxIcon from '@mui/icons-material/AddBox';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { ArrowDropDown, Edit, ScreenshotMonitor } from '@mui/icons-material';
import { AmountAndCurrency } from 'ui/AmountAndCurrency';
import { EOrderSchemeName } from 'redux/reducers/orderManagement/constants';
import { TOrderItemDetail, TOrderSplitItem } from 'redux/reducers/orderManagement/types';
import { ETableStructureType } from '../../../../ui/EasTable/types';
import { EasTableValue } from '../../../../ui/EasTable/EasTableRow/EasTableValue';
import { SplitItemTableDialog } from '../SplitItemTableDialog';
import { Total } from './OrderDetailsRowChild/Total';
import { splitItemTableStructure } from '../SplitItemTable/tableStructure';
import HelpButton from '../../../../ui/HelpButton';
import { COLORS } from '../../../../services/constants/colors';
import { EasCard } from '../../../../ui/EasCard';

type OrderSplitsCardProps = {
	splits: TOrderSplitItem[];
	loading: boolean;
	currency: string;
	splitItemsMap: Record<string, TOrderItemDetail[]>;
	splitItemsLoadingMap: Record<string, boolean>;
};

export const OrderSplitsCard: FC<OrderSplitsCardProps> = ({
	splits,
	loading,
	currency,
	splitItemsMap,
	splitItemsLoadingMap,
}) => {
	const [detalization, setDetalization] = useState(false);

	const handleDetalization = () => {
		setDetalization(!detalization);
	};

	const generateScreenshot = useCallback(async () => {
		const element = document.getElementById('order-splits-card');
		if (!element) return;

		const canvas = await html2canvas(element, {
			scale: 2,
			useCORS: true,
			backgroundColor: '#ffffff',
		});

		const link = document.createElement('a');
		link.download = 'order-splits.png';
		link.href = canvas.toDataURL('image/png');
		link.click();
	}, []);

	if (loading) {
		return <Skeleton variant="rectangular" height="350px" sx={{ borderRadius: '12px', mb: 2 }} />;
	}

	if (!splits.length) return null;

	// Calculate total amount across all splits
	const totalAmount = splits.reduce((acc, split) => acc + (split.total_item_cost ?? 0), 0);

	return (
		<EasCard
			id="order-splits-card"
			title={`Order Details (${splits.length})`}
			action={<></>}
			loading={loading}
		>
			{splits.map((split) => (
				<OrderSplitRow
					key={split.split_id}
					split={split}
					currency={currency}
					detalization={detalization}
					splitItems={splitItemsMap[split.split_id] ?? []}
					splitItemsLoading={splitItemsLoadingMap[split.split_id] ?? false}
				/>
			))}
		</EasCard>
	);
};

// ============ Order Split Row ============

type OrderSplitRowProps = {
	split: TOrderSplitItem;
	currency: string;
	detalization: boolean;
	splitItems: TOrderItemDetail[];
	splitItemsLoading: boolean;
};

const OrderSplitRow: FC<OrderSplitRowProps> = ({
	split,
	currency,
	detalization,
	splitItems,
	splitItemsLoading,
}) => {
	const [showAll, setShowAll] = useState(false);
	const [openSplitDialog, setOpenSplitDialog] = useState(false);

	const schemeName =
		EOrderSchemeName[split.scheme as keyof typeof EOrderSchemeName] ?? split.scheme;

	return (
		<Box
			sx={{
				border: '1px solid #00000020',
				borderRadius: '12px',
				padding: '10px',
				marginBottom: '10px',
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<Typography variant="body1" sx={{ fontWeight: 'bold' }} color="primary">
					{schemeName}
				</Typography>
				<Box sx={{ ml: 2 }}>
					<Typography
						variant="body2"
						color="primary"
						sx={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 'bold' }}
						onClick={() => setOpenSplitDialog(true)}
					>
						Show Split Item Table
					</Typography>
				</Box>
			</Box>

			<Box sx={{ ml: 2, mt: 1, display: 'flex', flexDirection: 'column', gap: 0.8 }}>
				{/* Split details as children */}
				{/* <SplitDetailRow
					label="Goods Cost"
					amount={split.goods_cost_provided_by_em}
					currency={currency}
					detalization={detalization}
				/>
				{split.tbe_cost_provided_by_em > 0 && (
					<SplitDetailRow
						label="TBE Cost"
						amount={split.tbe_cost_provided_by_em}
						currency={currency}
						detalization={detalization}
					/>
				)}
				<SplitDetailRow
					label="Delivery Cost"
					amount={split.delivery_cost}
					currency={currency}
					detalization={detalization}
				/> */}

				{/* {detalization && (
					<>
						<DutyRow label="EAS Fee" amount={split.eas_fee_value} currency={currency} />
						{split.vat_on_eas_fee > 0 && (
							<DutyRow
								label={`VAT on EAS Fee (${((split.vat_on_eas_fee / split.eas_fee_value) * 100).toFixed(0)}%)`}
								amount={split.vat_on_eas_fee}
								currency={currency}
							/>
						)}
						<DutyRow label="DPO Fee" amount={split.dpo_fee} currency={currency} />
						{split.vat_on_dpo_fee > 0 && (
							<DutyRow label="VAT on DPO Fee" amount={split.vat_on_dpo_fee} currency={currency} />
						)}
						{split.eas_fee_for_invoice > 0 && (
							<DutyRow
								label="EAS Fee for Invoice"
								amount={split.eas_fee_for_invoice}
								currency={currency}
							/>
						)}
						{split.fc_rep_vat_for_invoice > 0 && (
							<DutyRow
								label="FC Rep VAT for Invoice"
								amount={split.fc_rep_vat_for_invoice}
								currency={currency}
							/>
						)}
						{split.customs_duty_for_invoice > 0 && (
							<DutyRow
								label="Customs Duty for Invoice"
								amount={split.customs_duty_for_invoice}
								currency={currency}
							/>
						)}
						{split.delivery_conditions && (
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'row',
									justifyContent: 'space-between',
									gap: 4,
									ml: 4,
								}}
							>
								<Typography variant="body2" color="text.secondary">
									Delivery Conditions
								</Typography>
								<Typography variant="body2">{split.delivery_conditions}</Typography>
							</Box>
						)}
					</>
				)} */}

				{/* Split Items */}
				{splitItemsLoading && (
					<Skeleton variant="rectangular" height="60px" sx={{ borderRadius: '8px', mt: 1 }} />
				)}

				{splitItems.length > 0 && (
					<Box sx={{ mt: 1 }}>
						{/* <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold', mb: 0.5 }}>
							Split Items ({splitItems.length})
						</Typography> */}
						{splitItems.map((item, index) => {
							if (!showAll && index > 4) return null;
							return (
								<SplitItemRow
									key={item.item_split_id}
									item={item}
									currency={currency}
									detalization={detalization}
								/>
							);
						})}
						{splitItems.length > 5 && (
							<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
								<Typography
									variant="body2"
									sx={{ fontWeight: 'bold', cursor: 'pointer' }}
									color="primary"
									onClick={() => setShowAll(!showAll)}
								>
									{showAll ? 'Hide' : `Show all ${splitItems.length} items`}
								</Typography>
								<ArrowDropDown
									sx={{
										transform: showAll ? 'rotate(180deg)' : 'rotate(0deg)',
										transition: 'transform 0.3s ease',
									}}
									color="primary"
								/>
							</Box>
						)}
					</Box>
				)}
			</Box>

			{/* Total for this split */}
			<Divider sx={{ mt: 1, mb: 1, ml: 4 }} />
			<Box sx={{ display: 'flex', justifyContent: 'end' }}>
				<Typography variant="body1" sx={{ fontWeight: 'bold' }}>
					<AmountAndCurrency
						amount={splitItems.reduce(
							(acc, item) =>
								acc + (item.import_duty_value + item.vat_value + item.merchandise_cost),
							0,
						)}
						currency={currency}
						bold="bold"
						size={1}
					/>
				</Typography>
			</Box>
			<Box sx={{ display: 'flex', justifyContent: 'end' }}>
				<Typography variant="body1" sx={{ fontWeight: 'bold' }}>
					EAS Fee: &nbsp;&nbsp;&nbsp;
					<AmountAndCurrency
						amount={split.eas_fee_value ?? 0}
						currency={currency}
						bold="bold"
						size={1}
					/>
				</Typography>
			</Box>
			<SplitItemTableDialog
				open={openSplitDialog}
				onClose={() => setOpenSplitDialog(false)}
				title={`Split Items – ${schemeName}`}
				items={splitItems}
				loading={splitItemsLoading}
				currency={currency}
			/>
		</Box>
	);
};

// ============ Split Detail Row ============

type SplitDetailRowProps = {
	label: string;
	amount: number;
	currency: string;
	detalization: boolean;
};

const _SplitDetailRow: FC<SplitDetailRowProps> = ({ label, amount, currency, detalization }) => (
	<Box
		sx={{
			display: 'flex',
			flexDirection: 'row',
			justifyContent: 'space-between',
			gap: 2,
		}}
	>
		<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
			<Typography variant="body2">
				<span style={{ fontWeight: 'bold' }}>{label}</span>
			</Typography>
		</Box>

		{detalization ? (
			<Box sx={{ display: 'flex', gap: 1, alignSelf: 'end' }}>
				<AmountAndCurrency amount={amount ?? 0} currency={currency} bold="bold" size={0.9} />
			</Box>
		) : (
			<Box>
				<AmountAndCurrency amount={amount ?? 0} currency={currency} bold="bold" size={1} />
			</Box>
		)}
	</Box>
);

// ============ Duty Row ============

type DutyRowProps = {
	label: string;
	amount: number;
	currency: string;
};

const DutyRow: FC<DutyRowProps> = ({ label, amount, currency }) => {
	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'row',
				justifyContent: 'space-between',
				gap: 4,
				ml: 4,
			}}
		>
			<Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
				<Typography variant="body2" color="text.secondary">
					{label}
				</Typography>
			</Box>
			<AmountAndCurrency amount={amount ?? 0} currency={currency} size={0.8} />
		</Box>
	);
};

// ============ Split Item Row ============

type SplitItemRowProps = {
	item: TOrderItemDetail;
	currency: string;
	detalization: boolean;
};

const SplitItemRow: FC<SplitItemRowProps> = ({ item, currency, detalization }) => {
	const [openItemDialog, setOpenItemDialog] = useState(false);
	return (
		<Box sx={{ mb: 1 }}>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'space-between',
					gap: 2,
					alignItems: 'center',
					width: '100%',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'row',
						gap: 1,
						alignItems: 'center',
						width: '100%',
						justifyContent: 'start',
					}}
				>
					{!!item.type_of_goods && (
						<EasTableValue
							value={item.type_of_goods === 'GOODS' ? 'Goods' : 'TBE'}
							size="m"
							type={ETableStructureType.ICON}
							bold={true}
							tableStructureItem={splitItemTableStructure.find(
								(table) => table.selector === 'type_of_goods',
							)}
							tooltip={item.type_of_goods === 'GOODS' ? 'Goods' : 'TBE'}
						/>
					)}

					<Typography variant="body2" component="span" sx={{ whiteSpace: 'nowrap' }}>
						<span style={{ fontWeight: 'bold' }}>{item.id_provided_by_em}</span> -{' '}
						{item.short_description}
					</Typography>
					<EasTableValue
						value={item.location_warehouse_country}
						size="m"
						type={ETableStructureType.COUNTRY}
						bold={true}
					/>
					{/* <Typography
						variant="body2"
						color="primary"
						sx={{ cursor: 'pointer', textDecoration: 'underline', ml: 1, fontSize: '.75rem' }}
						onClick={() => setOpenItemDialog(true)}
					>
						Show Split Item Table
					</Typography> */}
					{/* <EasTableValue value="returned" size="m" type={ETableStructureType.STATUS} bold={true} /> */}
				</Box>

				{detalization ? (
					<Box sx={{ display: 'flex', gap: 1, alignSelf: 'end' }}>
						<Typography variant="body2" sx={{ fontSize: '.8rem' }} color="text.secondary">
							<AmountAndCurrency
								amount={item.cost_provided_by_em ?? 0}
								currency={currency}
								size={0.8}
							/>
							&nbsp;×&nbsp;{item.quantity}&nbsp;=&nbsp;
							{/* &nbsp;+&nbsp;Delivery:&nbsp;
							<AmountAndCurrency
								amount={item.prorated_delivery_cost ?? 0}
								currency={currency}
								size={0.8}
							/> */}
						</Typography>
						<AmountAndCurrency
							amount={item.cost_provided_by_em ?? 0}
							currency={currency}
							bold="bold"
							size={0.9}
						/>
					</Box>
				) : (
					<Box>
						<AmountAndCurrency
							amount={item.import_duty_value + item.vat_value + item.merchandise_cost}
							currency={currency}
							bold="bold"
							size={1}
						/>
					</Box>
				)}
			</Box>

			{detalization && (
				<Box sx={{ mt: 1 }}>
					{!!item.prorated_eas_fee && (
						<DutyRow label="EAS Fee" amount={item.prorated_eas_fee} currency={currency} />
					)}
					{!!item.prorated_dpo_fee && (
						<DutyRow label="DPO Fee" amount={item.prorated_dpo_fee} currency={currency} />
					)}
					{!!item.vat_value && (
						<DutyRow
							label={`Item VAT (${item.vat_rate}%)`}
							amount={item.vat_value}
							currency={currency}
						/>
					)}
					{!!item.prorated_delivery_cost && (
						<DutyRow
							label="Delivery Charge"
							amount={item.prorated_delivery_cost}
							currency={currency}
						/>
					)}
					{!!item.item_delivery_charge_vat && (
						<DutyRow
							label={`VAT on Delivery Charge (${item.vat_rate}%)`}
							amount={item.item_delivery_charge_vat}
							currency={currency}
						/>
					)}
					<Divider sx={{ mt: 0.4, mb: 0.4, ml: 4 }} />
					<Total
						totalPerItem={
							item.cost_provided_by_em +
							item.vat_value +
							item.prorated_delivery_cost +
							item.item_delivery_charge_vat +
							item.prorated_eas_fee +
							item.prorated_eas_fee_vat
						}
						quantity={item.quantity}
						totalCost={
							item.cost_provided_by_em +
							item.vat_value +
							item.prorated_delivery_cost +
							item.item_delivery_charge_vat +
							item.prorated_eas_fee +
							item.prorated_eas_fee_vat
						}
						paymentCurrency={currency}
					/>
				</Box>
			)}
			<SplitItemTableDialog
				open={openItemDialog}
				onClose={() => setOpenItemDialog(false)}
				title={`Split Item – ${item.id_provided_by_em}`}
				items={[item]}
				loading={false}
				currency={currency}
			/>
		</Box>
	);
};
